import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { EmptyState, type EmptyStateProps } from './EmptyState';
import { DegradedState } from './DegradedState';
import { LoadingState } from './LoadingState';
import { Button } from './Button';
import { useStateAction } from './useStateAction';
import { SourcePill } from './SourcePill';
import type {
  EmptySurfaceStateKind,
  OperationStateKind,
  SurfaceStateAction,
  TruthfulStateKind,
  SurfaceProvenance,
} from './SurfaceState.types';

export type {
  EmptySurfaceStateKind,
  OperationStateKind,
  SurfaceStateAction,
  TruthfulStateKind,
} from './SurfaceState.types';

export interface SurfaceStateProps {
  kind: TruthfulStateKind;
  title: string;
  description: string;
  action?: SurfaceStateAction;
  secondaryAction?: SurfaceStateAction;
  recoveryHint?: string;
  loadingVariant?: Parameters<typeof LoadingState>[0]['variant'];
  className?: string;
  provenance?: SurfaceProvenance;
}

const emptyPresentation: Record<
  EmptySurfaceStateKind,
  Pick<EmptyStateProps, 'illustration' | 'tone' | 'eyebrow'>
> = {
  idle: {
    illustration: 'filtered-empty',
    tone: 'neutral',
    eyebrow: 'Waiting for input',
  },
  'verified-empty': {
    illustration: 'verified-empty',
    tone: 'neutral',
    eyebrow: 'Checked and empty',
  },
  'filtered-empty': {
    illustration: 'filtered-empty',
    tone: 'neutral',
    eyebrow: 'No filter matches',
  },
  'first-use': {
    illustration: 'verified-empty',
    tone: 'neutral',
    eyebrow: 'Ready to configure',
  },
  permission: {
    illustration: 'permission',
    tone: 'attention',
    eyebrow: 'Permission required',
  },
  success: {
    illustration: 'complete',
    tone: 'positive',
    eyebrow: 'Complete',
  },
};

function SurfaceStatePresentation({
  kind,
  title,
  description,
  action,
  secondaryAction,
  recoveryHint,
  loadingVariant = 'skeleton-list',
  className,
}: SurfaceStateProps) {
  const recovery = useStateAction();
  if (kind === 'loading' || kind === 'submitting' || kind === 'queued' || kind === 'long-running') {
    return (
      <LoadingState
        variant={loadingVariant}
        label={title}
        description={description}
        className={className}
      />
    );
  }

  if (
    kind === 'unavailable' ||
    kind === 'degraded' ||
    kind === 'failure' ||
    kind === 'partial-success' ||
    kind === 'partial' ||
    kind === 'stale' ||
    kind === 'retrying'
  ) {
    return (
      <DegradedState
        title={title}
        description={description}
        severity={kind === 'unavailable' || kind === 'failure' ? 'error' : 'degraded'}
        stateKind={kind}
        recoveryHint={recoveryHint}
        onRetry={action ? () => void recovery.run(action.onClick) : undefined}
        isRetrying={kind === 'retrying' || recovery.pending}
        retryLabel={action?.label}
        className={className}
      >
        {recovery.failed ? <p role="alert">The action could not be completed. Try again.</p> : null}
        {secondaryAction ? (
          <Button className="mt-3" variant="ghost" size="sm" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        ) : null}
      </DegradedState>
    );
  }

  const presentation = emptyPresentation[kind];
  return (
    <EmptyState
      {...presentation}
      title={title}
      description={description}
      action={action}
      secondaryAction={secondaryAction}
      hint={recoveryHint}
      stateKind={kind}
      className={className}
    />
  );
}

export function SurfaceState(props: SurfaceStateProps) {
  return (
    <div data-surface-state={props.kind}>
      <SurfaceStatePresentation {...props} />
      {props.provenance ? <SourcePill className="mt-2" {...props.provenance} /> : null}
    </div>
  );
}

export interface OperationFeedbackProps {
  state: OperationStateKind;
  title: string;
  description: string;
  progress?: number;
  onCancel?: () => void | Promise<unknown>;
  onRetry?: () => void | Promise<unknown>;
  onConfirm?: () => void | Promise<unknown>;
  confirmLabel?: string;
  /** Caller supplies a durable, tenant-authorized route or in-page evidence anchor. */
  auditHref?: string;
  className?: string;
  children?: ReactNode;
}

export function OperationFeedback({
  state,
  title,
  description,
  progress,
  onCancel,
  onRetry,
  onConfirm,
  confirmLabel = 'Confirm',
  auditHref,
  className,
  children,
}: OperationFeedbackProps) {
  const action = useStateAction();
  const pending =
    state === 'submitting' || state === 'queued' || state === 'running' || state === 'retrying';
  const failed = state === 'failure';
  const canRetry = state === 'failure' || state === 'partial-success' || state === 'cancelled';
  const canCancel = pending || state === 'confirmation';
  const validProgress =
    progress !== undefined && Number.isFinite(progress) && progress >= 0 && progress <= 100;
  const safeAuditHref =
    auditHref &&
    (/^\/(?!\/)/.test(auditHref) || auditHref.startsWith('#')) &&
    !/[\\\s]/.test(auditHref)
      ? auditHref
      : undefined;
  return (
    <section
      role={failed ? 'alert' : 'status'}
      aria-live={failed ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-operation-pending={pending || action.pending}
      data-state-kind={state}
      className={clsx(
        'rounded-xl border border-[rgb(var(--color-border-primary))] bg-[rgb(var(--color-bg-secondary))] p-4',
        className
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-[rgb(var(--color-text-primary))]">{title}</h3>
          <p className="mt-1 text-xs leading-5 text-[rgb(var(--color-text-secondary))]">
            {description}
          </p>
        </div>
        {pending ? (
          <span
            className="mt-1 h-4 w-4 motion-loading-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        ) : null}
      </div>
      {validProgress ? (
        <div className="mt-3">
          <progress
            aria-label={`${title} progress`}
            className="h-2 w-full accent-[var(--dest-accent)]"
            max={100}
            value={progress}
          >
            {progress}%
          </progress>
          <p className="mt-1 text-xs tabular-nums text-[rgb(var(--color-text-tertiary))]">
            {progress}% complete
          </p>
        </div>
      ) : null}
      {children}
      {action.failed ? (
        <p role="alert">
          The action could not be completed. Check the latest operation status before trying again.
        </p>
      ) : null}
      {safeAuditHref ? (
        <a className="mt-3 inline-block text-sm underline" href={safeAuditHref}>
          View operation evidence
        </a>
      ) : null}
      {onCancel || onRetry || onConfirm ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {state === 'confirmation' && onConfirm ? (
            <Button size="sm" disabled={action.pending} onClick={() => void action.run(onConfirm)}>
              {confirmLabel}
            </Button>
          ) : null}
          {onRetry && canRetry ? (
            <Button
              size="sm"
              variant="outline"
              disabled={action.pending}
              onClick={() => void action.run(onRetry)}
            >
              Try again
            </Button>
          ) : null}
          {onCancel && canCancel ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={action.pending}
              onClick={() => void action.run(onCancel)}
            >
              Cancel
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
